// Read-axis contracts - the "did you read the GAME" grading axis (Axis B) + the Observe/Guided/Solo
// spine. North Star §5/§7; ENGINE_DESIGN §7.4 (dimensions/error codes), §4.2 (narrowing). D3 authors
// the table types (field guide §12), the read/diagnosis record shapes, the table-type presets, and
// the two new skill dimensions. NO engine logic here - data + contracts only, additive before
// S5/S7/S9 (mirrors D2). zod is the single source of truth; every exported type = z.infer.
import { z } from 'zod';
import { ArchetypeIdSchema, ImbalanceProfileSchema } from './archetype';
import { StreetSchema } from './position';
import { SizingIntentSchema } from './action';
// ── table types (field guide §12) ──────────────────────────────────────────────
/** The seven $1/$3 table textures from the Kirkland field guide §12. The scenario generator builds a
 *  table of a KNOWN type from a preset (below); Axis B grades the user's table-read against it. */
export const TABLE_TYPES = [
    'loosePassive', // §12.1 - lots of limping/calling, few re-raises, 5-6 to the flop
    'limpHeavy', // §12.2 - 3-4 limpers, cheap flops, pots only build with strength
    'callingStation', // §12.3 - everyone calls down light; bluffs never get through
    'tight', // §12.4 - lots of folding, raises get respect, few multiway pots
    'aggressiveReg', // §12.5 - frequent raising/3-betting, limpers attacked, pots big fast
    'promoHeavy', // §12.6 - high-hand jackpot chasers, weird holdings, paired/flushy-board danger
    'deepSplashy', // §12.7 - $800+ stacks, splashy calling, implied-odds hands go up
];
export const TableTypeSchema = z.enum(TABLE_TYPES);
// ── learning spine + context (North Star §5) ───────────────────────────────────
/** The progressive Local spine: Observe (pro auto-plays + narrates, no grading) → Guided (you act on
 *  a ramp, lock in reads, get coaching) → Solo (you fly the table, graded). Config, not new engines. */
export const LearningLayerSchema = z.enum(['observe', 'guided', 'solo']);
/** Local (the Caribbean $1/$3 room - all of v1) vs Pro (pros-vs-pros - architected now, later phase). */
export const TrainingContextSchema = z.enum(['local', 'pro']);
// ── skill dimensions + read error codes (ENGINE_DESIGN §7.4 dimension-map extension) ───────────────
/** The read dimensions D3 adds to the spec §11 grading map. `player_profiling` extends the existing
 *  `IGNORED_CONFIRMED_READ` row (§7.4); `table_reading` is wholly new. `condition_reading` (ST3) is the
 *  tournament-condition axis - reading effective stack, stage, and ICM pressure, co-equal with the
 *  player read. Snake_case to match the existing dimension keys (e.g. `value_extraction`). */
export const READ_DIMENSIONS = ['table_reading', 'player_profiling', 'condition_reading'];
export const ReadDimensionSchema = z.enum(READ_DIMENSIONS);
/** Read-axis error codes appended to the §7.4 map. Stored as strings in the existing free-form
 *  `errorCodes` arrays (records.ts / facts.ts), but enumerated here so Axis B attribution is closed. */
export const READ_ERROR_CODES = [
    // player_profiling (extends IGNORED_CONFIRMED_READ)
    'IGNORED_CONFIRMED_READ', // acted against a confirmed player tag (existing §7.4 code, now homed here)
    'MISREAD_ARCHETYPE', // assigned the wrong archetype vs the scripted one
    'MARRIED_FIRST_READ', // kept a tag after showdown evidence contradicted it (never revised)
    'OVER_TAGGED_THIN', // locked a confident read on too little evidence (first-30-min discipline, §21)
    'MISREAD_VILLAIN_RANGE', // range-read grid far from the engine's narrowed range
    // table_reading (new dimension)
    'MISREAD_TABLE_TYPE', // guessed the wrong table type vs ground truth
    'MISSED_TABLE_ADJUSTMENT', // didn't adjust to the diagnosed table (e.g. bluffed into a station table)
    'WRONG_TABLE_SELECTION', // stayed at / entered a table type that plays against the plan (§12.4)
    // condition_reading (ST3 - the tournament-condition axis)
    'MISREAD_STAGE', // read the wrong tournament stage (e.g. called it 'middle' on the bubble)
    'MISREAD_STACK_DEPTH', // misjudged the effective-stack band (e.g. 'deep' when it is push/fold depth)
    'MISREAD_ICM_PRESSURE', // misjudged the ICM pressure (e.g. 'none' when the bubble tax is severe)
];
export const ReadErrorCodeSchema = z.enum(READ_ERROR_CODES);
/** Every read error code → its dimension. The `Record<ReadErrorCode, …>` annotation makes exhaustive
 *  coverage a COMPILE-TIME guarantee (a new code with no dimension fails `tsc`); the test re-checks it
 *  at runtime. This is the ENGINE_DESIGN §7.4 table, extended for the read axis. */
export const READ_ERROR_DIMENSION = {
    IGNORED_CONFIRMED_READ: 'player_profiling',
    MISREAD_ARCHETYPE: 'player_profiling',
    MARRIED_FIRST_READ: 'player_profiling',
    OVER_TAGGED_THIN: 'player_profiling',
    MISREAD_VILLAIN_RANGE: 'player_profiling',
    MISREAD_TABLE_TYPE: 'table_reading',
    MISSED_TABLE_ADJUSTMENT: 'table_reading',
    WRONG_TABLE_SELECTION: 'table_reading',
    MISREAD_STAGE: 'condition_reading',
    MISREAD_STACK_DEPTH: 'condition_reading',
    MISREAD_ICM_PRESSURE: 'condition_reading',
};
// ── lock-in / revise state ──────────────────────────────────────────────────────
/** A read starts `open`, is `lockedIn` when the user commits it, and becomes `revised` if changed
 *  after lock-in. Revision TIMING is captured (revisionCount / lockedAtDecisionIndex) but NOT scored
 *  in v1 (North Star §5; BUILD_PLAN D3 hard constraint) - S9B reads state, never grades the timing. */
export const ReadLockStateSchema = z.enum(['open', 'lockedIn', 'revised']);
export const ReadLockSchema = z.object({
    state: ReadLockStateSchema,
    /** how many times the guess changed before its final lock-in. Captured, unscored in v1. */
    revisionCount: z.number().int().nonnegative(),
    /** decision index at which the read was locked in (for later timing analysis; unscored in v1). */
    lockedAtDecisionIndex: z.number().int().nonnegative().optional(),
});
// ── read / diagnosis GUESSES (what the user asserts; graded by S9B against the scripted ground truth) ──
/** Per-seat player read: a guessed archetype (+ optional blend, mirroring ScenarioSpec villains),
 *  optional per-axis imbalance guesses (the partial-credit surface for S9B "adjacent" reads), and
 *  free behavior tags (memory hooks, North Star §5). */
export const PlayerReadGuessSchema = z.object({
    seat: z.number().int().nonnegative(),
    guessedArchetypeId: ArchetypeIdSchema,
    blend: z
        .array(z.object({ archetypeId: ArchetypeIdSchema, weight: z.number().min(0).max(1) }))
        .max(3)
        .optional(),
    /** partial per-axis imbalance guess - S9B gives partial credit for imbalance-axis overlap. */
    imbalanceGuess: ImbalanceProfileSchema.partial().optional(),
    /** free-form behavior tags the user attached to this seat. */
    tags: z.array(z.string()).optional(),
    lock: ReadLockSchema,
});
/** Table-type read: the guessed §12 table type + optional supporting observations (§21 cues). */
export const TableReadGuessSchema = z.object({
    guessedTableType: TableTypeSchema,
    notes: z.array(z.string()).optional(),
    lock: ReadLockSchema,
});
/** Range read: the user's painted grid for one seat on one street - canonical hand notation
 *  (e.g. 'AKs', 'TT', '72o') → weight 0..1; an absent cell means weight 0. S9B compares this to the
 *  engine's narrowed WeightedRange (§4.2) via Mode C's distance metric. A JSON-friendly grid (the
 *  engine's Float32Array WeightedRange is non-serializable, so guesses use notation keys). */
export const RangeReadGuessSchema = z.object({
    seat: z.number().int().nonnegative(),
    street: StreetSchema,
    grid: z.record(z.string(), z.number().min(0).max(1)),
    lock: ReadLockSchema,
});
// ── verdict / score shapes (Axis B output; S9B fills these) ─────────────────────
/** Was the read right, adjacent, or wrong. `partial` = imbalance-axis or table-family overlap. */
export const ReadCorrectnessSchema = z.enum(['correct', 'partial', 'wrong']);
/** One graded read, parallel to the hand-grade (ENGINE_DESIGN §7.3 1-10 band). Every errorCode is a
 *  READ_ERROR_CODE and attributes to `dimension` via READ_ERROR_DIMENSION. */
export const ReadVerdictSchema = z.object({
    dimension: ReadDimensionSchema,
    correctness: ReadCorrectnessSchema,
    score: z.number().min(1).max(10),
    errorCodes: z.array(ReadErrorCodeSchema),
    /** the engine's scripted ground truth (un-hallucinatable), surfaced in the debrief. */
    groundTruth: z.string().optional(),
    note: z.string().optional(),
});
/** The composite Axis-B result for a hand/table - the read-grade that mirrors the hand-grade. */
export const ReadGradeSchema = z.object({
    players: z.array(ReadVerdictSchema).optional(),
    table: ReadVerdictSchema.optional(),
    ranges: z.array(ReadVerdictSchema).optional(),
    /** tournament-condition reads (ST3): the stage / effective-stack / ICM-pressure sub-reads, each a
     *  `condition_reading` verdict. Absent in cash spots (no tournament condition to read). */
    conditions: z.array(ReadVerdictSchema).optional(),
    /** Axis B composite, 1-10; absent until enough reads are locked in. */
    axisBScore: z.number().min(1).max(10).optional(),
});
// ── table-type presets (DATA schema; data/table-types/table-types.v1.json) ──────
/** A table type → the archetype mix + dynamics/sizing defaults the scenario generator uses to build a
 *  table of that KNOWN type - the ground truth Axis B grades against. `archetypeMix` weights are
 *  relative sampling frequencies (need not sum to 1); every archetypeId must resolve against
 *  ARCHETYPE_IDS (validate.test.ts enforces). Sizing/dynamics carry NO dollar amounts (pot-fraction
 *  intents only) and NO WA bet-cap numbers (deferred post-v1). */
export const TableTypePresetSchema = z.object({
    tableType: TableTypeSchema,
    label: z.string(),
    description: z.string(),
    archetypeMix: z
        .array(z.object({ archetypeId: ArchetypeIdSchema, weight: z.number().positive() }))
        .min(1),
    dynamics: z.object({
        /** 0 = nobody limps, 1 = everybody limps (§12). */
        limpiness: z.number().min(0).max(1),
        /** 0 = passive, 1 = constant raising/3-betting (§12.5). */
        aggression: z.number().min(0).max(1),
        /** 0 = folds to pressure, 1 = calls down light (§12.3). */
        stickiness: z.number().min(0).max(1),
        /** typical number of players seeing the flop (§12.1 "five or six"). */
        avgPlayersToFlop: z.number().min(2).max(9),
    }),
    sizing: z.object({
        /** hero's default open intent at this table (bigger over limpers, §12.2). */
        defaultOpenIntent: SizingIntentSchema,
        /** 0-1 value-bet emphasis (higher on stations, §12.3). */
        valueBias: z.number().min(0).max(1),
        /** 0-1 bluff emphasis (lower on stations/loose-passive, §12.1/§12.3). */
        bluffBias: z.number().min(0).max(1),
    }),
    /** stakes preset ids (stakes.v1.json) this table type commonly appears at; cross-ref only. */
    commonStakes: z.array(z.string()).optional(),
    source: z.string(),
});
/** table-types.v1.json as a versioned unit - one preset per TABLE_TYPES value (all seven reachable). */
export const TableTypePresetTableSchema = z.object({
    version: z.number().int(),
    presets: z.array(TableTypePresetSchema),
});
//# sourceMappingURL=read.js.map